import { useEffect, useMemo, useState } from 'react';
import { getSafeSupabaseClient } from '@/lib/supabaseSync';
import { useTenantStore } from '@/stores/prioritization/tenantStore';

export interface WorkspaceRosterUser {
  id: string;
  name: string;
  email: string;
}

export interface WorkspaceRosterGroup {
  id: string;
  name: string;
}

/**
 * Every user and group in the workspace — the candidate pool for custom path
 * stops.
 *
 * Deliberately unfiltered by org node, unlike `useOrgNodeRoster`: a custom path
 * exists precisely so the designer can route past the org chart, or work in a
 * workspace that has none, so "valid at that node" has nothing to filter on.
 */
export function useWorkspaceRoster(enabled: boolean) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [users, setUsers] = useState<WorkspaceRosterUser[]>([]);
  const [groups, setGroups] = useState<WorkspaceRosterGroup[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !workspaceId || !enabled) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [{ data: rpcData }, { data: groupData }] = await Promise.all([
          supabase.rpc('get_workspace_participants', { p_workspace_id: workspaceId }),
          supabase
            .from('groups')
            .select('id, name, display_name')
            .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
            .order('name'),
        ]);
        if (cancelled) return;

        setUsers(
          (rpcData ?? []).map((u: Record<string, unknown>) => ({
            id: u.id as string,
            name: ((u.name as string) || (u.email as string)) ?? '',
            email: (u.email as string) ?? '',
          })),
        );
        setGroups(
          (groupData ?? []).map((g: { id: string; name?: string; display_name?: string }) => ({
            id: g.id,
            name: g.display_name || g.name || g.id,
          })),
        );
      } catch (e) {
        console.warn('[useWorkspaceRoster] Failed to load roster:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, enabled]);

  return useMemo(() => ({ loading, users, groups }), [loading, users, groups]);
}
